import React, { useEffect, useState } from 'react';
import { MessageCircle, Swords, UserPlus, X } from 'lucide-react';
import { formatPublicPlayerId } from '../components/LobbyProfileScreen';
import { getApiBaseUrl } from '../lib/apiBaseUrl';
import { cn } from './utils/cn';

/**
 * Пытается подтянуть публичный профиль с REST (если бэкенд отдаёт один из путей).
 * @param {string} apiBaseUrl
 * @param {string} accessToken
 * @param {string} userId
 * @returns {Promise<null | {
 *   id: string,
 *   username: string,
 *   avatarUrl: string | null,
 *   clan: string,
 *   level: number,
 *   xpCurrent: number | null,
 *   xpMax: number | null,
 *   email: string | null,
 *   balance: number | null,
 *   bucksBalance: number | null,
 * }>}
 */
async function tryFetchPublicProfile(apiBaseUrl, accessToken, userId) {
  if (!userId || !accessToken) return null;
  const base = apiBaseUrl ?? '';
  const headers = { Authorization: `Bearer ${accessToken}` };
  const paths = [
    `/api/users/${encodeURIComponent(userId)}/public`,
    `/api/user/public/${encodeURIComponent(userId)}`,
    `/api/players/${encodeURIComponent(userId)}/public`,
  ];
  for (const path of paths) {
    try {
      const res = await fetch(`${base}${path}`, { headers });
      if (!res.ok) continue;
      const data = await res.json();
      const u = data?.user ?? data?.profile ?? (data?.username ? data : null);
      if (!u || typeof u !== 'object') continue;
      const id = String(u.id ?? u.userId ?? userId);
      const username = u.username ?? u.name ?? 'Игрок';
      return {
        id,
        username,
        avatarUrl: u.avatarUrl ?? u.avatar ?? null,
        clan: u.clan ?? u.clanName ?? 'Без клана',
        level: Number(u.level) || 1,
        xpCurrent: Number.isFinite(Number(u.xpCurrent)) ? Number(u.xpCurrent) : null,
        xpMax: Number.isFinite(Number(u.xpMax)) ? Number(u.xpMax) : null,
        email: typeof u.email === 'string' && u.email.trim() ? u.email.trim() : null,
        balance: u.balance != null ? Number(u.balance) : null,
        bucksBalance: u.bucksBalance != null ? Number(u.bucksBalance) : null,
      };
    } catch {
      /* try next path */
    }
  }
  return null;
}

/** Профиль с места за столом: бот (локально), вы, или онлайн-игрок (с попыткой REST). */
export function TableSeatProfileModal({
  open,
  onClose,
  variant,
  displayName = '',
  chips,
  userId,
  /** Аватар со стола / снимка (пока грузится REST или если API нет). */
  initialAvatarUrl = '',
  accessToken,
  apiBaseUrl,
  currentUserId,
  onSendFriendRequest,
  extendedProfile = null,
  actionsMode = 'default',
  onChallengeAction,
  onMessageAction,
}) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [usedFallback, setUsedFallback] = useState(false);
  const resolvedBase = apiBaseUrl ?? getApiBaseUrl();

  useEffect(() => {
    if (!open) {
      setProfile(null);
      setUsedFallback(false);
      setLoading(false);
      return undefined;
    }
    if (variant !== 'human' || !userId) {
      setProfile(null);
      setUsedFallback(false);
      setLoading(false);
      return undefined;
    }
    const seedAv = String(initialAvatarUrl || '').trim();
    let cancelled = false;
    (async () => {
      setLoading(true);
      const fetched = await tryFetchPublicProfile(resolvedBase, accessToken, userId);
      if (cancelled) return;
      if (fetched) {
        const fromApi = fetched.avatarUrl && String(fetched.avatarUrl).trim();
        setProfile({
          ...fetched,
          avatarUrl: fromApi || seedAv || null,
        });
        setUsedFallback(false);
      } else {
        setProfile({
          id: userId,
          username: displayName || 'Игрок',
          avatarUrl: seedAv || null,
          clan: 'Без клана',
          level: 1,
          xpCurrent: null,
          xpMax: null,
          email: null,
          balance: null,
          bucksBalance: null,
        });
        setUsedFallback(true);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, variant, userId, accessToken, resolvedBase, displayName, initialAvatarUrl]);

  if (!open) return null;

  const showFriend =
    typeof onSendFriendRequest === 'function' &&
    variant === 'human' &&
    profile?.id &&
    currentUserId &&
    profile.id !== String(currentUserId) &&
    profile.id !== 'system';
  const showFriendActions =
    actionsMode === 'friend' &&
    variant === 'human' &&
    profile?.id &&
    profile.id !== String(currentUserId) &&
    profile.id !== 'system' &&
    (typeof onChallengeAction === 'function' || typeof onMessageAction === 'function');

  const botGreeting = (name) =>
    `Привет! Меня зовут ${name || 'бот'}. Я умею играть в секу, белот и покер. Поиграем?`;

  return (
    <div
      className="fixed inset-0 z-[130] overflow-y-auto overscroll-contain bg-black/65 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="table-seat-profile-title"
      onClick={onClose}
    >
      <div className="flex min-h-[100dvh] w-full items-center justify-center px-3 py-6" onClick={(e) => e.stopPropagation()}>
        <section className="w-full max-w-sm max-h-[min(85dvh,calc(100dvh-1.5rem))] overflow-y-auto rounded-2xl border border-cyan-300/25 bg-gradient-to-b from-[#132238] to-[#0b1320] p-3 shadow-[0_16px_44px_rgba(0,0,0,0.55)]">
          <div className="mb-3 flex items-start justify-between gap-2">
            <p id="table-seat-profile-title" className="text-sm font-black uppercase tracking-wide text-cyan-200">
              {variant === 'bot' && 'Профиль бота'}
              {variant === 'self' && 'Ваше место'}
              {variant === 'human' && 'Профиль игрока'}
            </p>
            <button
              type="button"
              onClick={onClose}
              className="lobby-focus flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-white/15 bg-black/25 text-slate-300"
              aria-label="Закрыть"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>

          {variant === 'bot' ? (
            <div className="space-y-3 rounded-xl border border-white/10 bg-black/25 p-4 text-sm leading-relaxed text-slate-200">
              <div className="mx-auto flex h-20 w-20 items-center justify-center overflow-hidden rounded-2xl border border-amber-400/40 bg-black/40">
                <img
                  src={`${process.env.PUBLIC_URL || ''}/avatar-for-bot.png`}
                  alt=""
                  width={160}
                  height={160}
                  draggable={false}
                  className="h-full w-full object-cover"
                />
              </div>
              <p>{botGreeting(displayName)}</p>
            </div>
          ) : null}

          {variant === 'self' ? (
            <div className="space-y-2 rounded-xl border border-white/10 bg-black/25 p-4 text-sm text-slate-200">
              {String(initialAvatarUrl || '').trim() ? (
                <div className="mx-auto mb-2 flex h-16 w-16 overflow-hidden rounded-xl border border-amber-400/40 bg-black/40">
                  <img
                    src={String(initialAvatarUrl).trim()}
                    alt=""
                    className="h-full w-full object-cover"
                    draggable={false}
                  />
                </div>
              ) : null}
              <p>
                Вы за столом как <span className="font-bold text-white">{displayName || 'Игрок'}</span>.
              </p>
              {chips != null && Number.isFinite(Number(chips)) ? (
                <p className="text-xs text-slate-400">
                  Стек: <span className="font-mono font-semibold text-emerald-200">{Number(chips).toLocaleString('ru-RU')} SEKA</span>
                </p>
              ) : null}
            </div>
          ) : null}

          {variant === 'human' ? (
            <div className="space-y-3">
              {loading ? (
                <p className="py-6 text-center text-sm text-slate-400">Загрузка профиля…</p>
              ) : (
                <>
                  {usedFallback ? (
                    <p className="rounded-lg border border-amber-500/25 bg-amber-500/10 px-2 py-1.5 text-[11px] text-amber-100/95">
                      Сервер не отдал расширенный профиль — показаны данные со стола. Когда появится API публичного профиля,
                      здесь отобразятся аватар, уровень и клан.
                    </p>
                  ) : null}
                  <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-black/25 p-3">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-amber-500/50 bg-gradient-to-br from-[#1e3a5f] to-[#0f1c2e] text-xl font-black text-white">
                      {profile?.avatarUrl ? (
                        <img src={profile.avatarUrl} alt="" className="h-full w-full object-cover" draggable={false} />
                      ) : (
                        (profile?.username || displayName || 'G').charAt(0).toUpperCase()
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-base font-bold text-white">{profile?.username || displayName}</p>
                      <p className="text-xs text-cyan-200/90">Клан: {profile?.clan || 'Без клана'}</p>
                      <p className="text-xs text-slate-300">Уровень: {Number(profile?.level) || 1}</p>
                      {profile?.xpCurrent != null && profile?.xpMax != null && profile.xpMax > 0 ? (
                        <div className="mt-2">
                          <div className="mb-0.5 flex justify-between text-[10px] text-slate-400">
                            <span>Опыт</span>
                            <span className="tabular-nums">
                              {profile.xpCurrent} / {profile.xpMax}
                            </span>
                          </div>
                          <div className="h-1.5 overflow-hidden rounded-full bg-black/40">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-emerald-400"
                              style={{
                                width: `${Math.min(100, Math.max(0, (profile.xpCurrent / profile.xpMax) * 100))}%`,
                              }}
                            />
                          </div>
                        </div>
                      ) : null}
                      <p className="mt-1.5 truncate font-mono text-[10px] text-slate-500" title={profile?.id || userId}>
                        ID: {formatPublicPlayerId(profile?.id || userId)}
                      </p>
                      {profile?.email ? (
                        <p className="mt-1 truncate text-[11px] text-slate-400">{profile.email}</p>
                      ) : null}
                      {(profile?.balance != null && Number.isFinite(profile.balance)) ||
                      (profile?.bucksBalance != null && Number.isFinite(profile.bucksBalance)) ? (
                        <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-slate-300">
                          {profile.balance != null && Number.isFinite(profile.balance) ? (
                            <span>Фишки: {Number(profile.balance).toLocaleString('ru-RU')}</span>
                          ) : null}
                          {profile.bucksBalance != null && Number.isFinite(profile.bucksBalance) ? (
                            <span>Bucks: {Number(profile.bucksBalance).toLocaleString('ru-RU')}</span>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  {chips != null && Number.isFinite(Number(chips)) ? (
                    <p className="text-center text-xs text-slate-400">
                      За столом сейчас:{' '}
                      <span className="font-mono font-semibold text-emerald-200">{Number(chips).toLocaleString('ru-RU')} SEKA</span>
                    </p>
                  ) : null}
                  {extendedProfile ? (
                    <div className="space-y-2 rounded-xl border border-white/10 bg-black/25 p-3">
                      <p className="text-[10px] font-black uppercase tracking-[0.12em] text-amber-200/90">
                        Расширенный профиль
                      </p>
                      <div className="grid grid-cols-1 gap-1.5 text-xs text-slate-200">
                        <p>
                          Звание: <span className="font-semibold text-white">{extendedProfile.rankTitle || 'Нет данных'}</span>
                        </p>
                        <p>
                          Награды: <span className="font-semibold text-white">{extendedProfile.rewardsLabel || 'Нет данных'}</span>
                        </p>
                      </div>
                      {Array.isArray(extendedProfile.stats) && extendedProfile.stats.length ? (
                        <ul className="space-y-1 text-[11px]">
                          {extendedProfile.stats.map((row, index) => (
                            <li key={`${row?.label || 'stat'}-${index}`} className="flex items-center justify-between gap-2 rounded-md border border-white/10 bg-white/[0.03] px-2 py-1.5">
                              <span className="text-slate-300">{row?.label || 'Параметр'}</span>
                              <span className="text-right font-semibold text-white">{row?.value || 'Нет данных'}</span>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  ) : null}
                </>
              )}

              {showFriendActions ? (
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => {
                      onChallengeAction?.(profile);
                      onClose();
                    }}
                    className={cn(
                      'inline-flex w-full items-center justify-center gap-2 rounded-xl border border-emerald-400/40',
                      'bg-gradient-to-b from-emerald-500 to-emerald-800 px-4 py-3 text-sm font-bold uppercase tracking-wide text-white',
                      'shadow-[0_8px_24px_rgba(16,185,129,0.28)] transition hover:brightness-110'
                    )}
                  >
                    <Swords className="h-4 w-4" aria-hidden />
                    Отправить вызов
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      onMessageAction?.(profile);
                      onClose();
                    }}
                    className={cn(
                      'inline-flex w-full items-center justify-center gap-2 rounded-xl border border-cyan-400/40',
                      'bg-gradient-to-b from-cyan-500 to-cyan-800 px-4 py-3 text-sm font-bold uppercase tracking-wide text-white',
                      'shadow-[0_8px_24px_rgba(6,182,212,0.28)] transition hover:brightness-110'
                    )}
                  >
                    <MessageCircle className="h-4 w-4" aria-hidden />
                    Отправить маляву
                  </button>
                </div>
              ) : null}
              {!showFriendActions && showFriend ? (
                <button
                  type="button"
                  onClick={() => {
                    onSendFriendRequest(profile);
                    onClose();
                  }}
                  className={cn(
                    'inline-flex w-full items-center justify-center gap-2 rounded-xl border border-emerald-400/40',
                    'bg-gradient-to-b from-emerald-500 to-emerald-800 px-4 py-3 text-sm font-bold uppercase tracking-wide text-white',
                    'shadow-[0_8px_24px_rgba(16,185,129,0.28)] transition hover:brightness-110'
                  )}
                >
                  <UserPlus className="h-4 w-4" aria-hidden />
                  Отправить запрос закорефаниться
                </button>
              ) : null}
            </div>
          ) : null}
        </section>
      </div>
    </div>
  );
}
